import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, Instagram, Facebook, Youtube, MapPin } from "lucide-react";
import radioConfig from "../config/radioConfig";

const buildItems = () => {
  const { contact, social } = radioConfig;
  const items = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      value: contact.whatsapp,
      href: contact.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}` : "",
    },
    {
      id: "phone",
      label: "Teléfono",
      icon: Phone,
      value: contact.phone,
      href: contact.phone ? `tel:${contact.phone.replace(/\s/g, "")}` : "",
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      value: contact.email,
      href: contact.email ? `mailto:${contact.email}` : "",
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      value: social.facebook,
      href: social.facebook,
    },
  ];
  return items.filter((item) => Boolean(item.value));
};

export const Contact = () => {
  const items = buildItems();

  return (
    <section id="contacto" className="relative py-20 sm:py-28 bg-[#070A0F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-cyan-400 mb-3">
            Contacto
          </p>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white">
            Conectá con la radio
          </h2>
          <p className="mt-4 flex items-center gap-2 text-slate-400 font-mono text-sm">
            <MapPin size={15} className="text-cyan-400" />
            {radioConfig.city}, {radioConfig.province}, {radioConfig.country}
          </p>
        </motion.div>

        <div
          data-testid="contact-section"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {items.map((item, i) => {
            const Icon = item.icon;
            const available = Boolean(item.value);
            const inner = (
              <div className="flex items-center gap-4 w-full">
                <span
                  className={`flex-shrink-0 h-12 w-12 rounded-xl flex items-center justify-center border transition-colors duration-300 ${
                    available
                      ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400 group-hover:bg-cyan-500/20"
                      : "bg-slate-800/40 border-slate-800 text-slate-600"
                  }`}
                >
                  <Icon size={20} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className={`text-sm truncate font-mono ${available ? "text-cyan-400" : "text-slate-600"}`}>
                    {available ? item.value : "Pendiente de cargar"}
                  </p>
                </div>
              </div>
            );

            return available ? (
              <motion.a
                key={item.id}
                href={item.href}
                {...(item.id !== "phone" && item.id !== "email"
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                data-testid={`contact-${item.id}-item`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group rounded-2xl border border-slate-800 bg-[#131C2E] p-5 hover:border-cyan-500/40 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              >
                {inner}
              </motion.a>
            ) : (
              <motion.div
                key={item.id}
                data-testid={`contact-${item.id}-item`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-dashed border-slate-800 bg-[#0D131E]/50 p-5"
              >
                {inner}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Contact;
